import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { gatewayChatCompletionsUrl } from '../lib/gatewayUrl';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { websiteUrl } from '../lib/websiteUrl';
import {
  quickStartApiKeyExportBash,
  quickStartApiKeyExportPowerShell,
  quickStartInstall,
  quickStartSnippet,
  type QuickStartLang,
} from '../lib/modelQuickStart';
import { useCopyToClipboard } from '../lib/useCopyToClipboard';
import { PageLoader } from './PageLoader';

const LANGS: QuickStartLang[] = ['curl', 'python', 'node'];
const KEYS_PAGE_SIZE = 25;
const KEYS_PAGE_CAP = 20;

type KeyPresence = 'loading' | 'yes' | 'no' | 'error';

function CodeSnippet({ code, copyLabel, copiedLabel }: { code: string; copyLabel: string; copiedLabel: string }) {
  const { copied, copy } = useCopyToClipboard();
  const inline = !code.includes('\n');

  return (
    <div className={`code-snippet-box${inline ? ' code-snippet-box--inline' : ''}`}>
      <pre>
        <code>{code}</code>
      </pre>
      <button type="button" className="ghost code-snippet-copy" onClick={() => void copy(code)}>
        {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  );
}

export function ModelQuickStartView({
  modelId,
  onBack,
  onClose,
}: {
  modelId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const { dict } = useLanguage();
  const qs = dict.models.quickStart;
  const [lang, setLang] = useState<QuickStartLang>('curl');
  const [keyPresence, setKeyPresence] = useState<KeyPresence>('loading');
  const tabRefs = useRef<Partial<Record<QuickStartLang, HTMLButtonElement | null>>>({});

  const gatewayReady = gatewayChatCompletionsUrl() != null;
  const snippet = gatewayReady ? quickStartSnippet(modelId, lang) : null;
  const install = quickStartInstall(lang);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        let cursor: string | undefined;
        for (let page = 0; page < KEYS_PAGE_CAP; page += 1) {
          const res = await api.keys({ limit: KEYS_PAGE_SIZE, cursor });
          if (cancelled) return;
          if (res.data.some((k) => k.status === 'active')) {
            setKeyPresence('yes');
            return;
          }
          const next = res.page.next_cursor;
          if (!next) {
            setKeyPresence('no');
            return;
          }
          cursor = next;
        }
        if (!cancelled) setKeyPresence('no');
      } catch {
        if (!cancelled) setKeyPresence('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tabLabel: Record<QuickStartLang, string> = {
    curl: qs.tabCurl,
    python: qs.tabPython,
    node: qs.tabNode,
  };

  function onTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = LANGS.indexOf(lang);
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % LANGS.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + LANGS.length) % LANGS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = LANGS.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const id = LANGS[next]!;
    setLang(id);
    tabRefs.current[id]?.focus();
  }

  return (
    <div className="model-quickstart">
      <div className="model-quickstart-nav">
        <button type="button" className="ghost model-quickstart-back" onClick={onBack}>
          {qs.back}
        </button>
        {websiteUrl() && (
          <a
            href={`${websiteUrl()}docs#connect`}
            target="_blank"
            rel="noopener noreferrer"
            className="guide-link-btn"
          >
            {dict.guide.keysConnectDocsLabel}
          </a>
        )}
      </div>

      <p className="note model-quickstart-lede">{qs.subtitle}</p>

      <section className="quickstart-step" aria-labelledby="quickstart-step-1">
        <h3 id="quickstart-step-1" className="quickstart-step-heading">
          <span className="quickstart-step-num">1</span>
          {qs.step1Title}
        </h3>
        {keyPresence === 'loading' && <PageLoader compact />}
        {keyPresence === 'yes' && <p className="note">{qs.step1HasKey}</p>}
        {keyPresence === 'error' && (
          <>
            <p className="err">{qs.keysLoadError}</p>
            <Link to="/keys" className="btn-block model-quickstart-cta">
              {qs.keysPageCta}
            </Link>
          </>
        )}
        {keyPresence === 'no' && (
          <>
            <p className="note">{qs.step1NoKey}</p>
            <Link to="/keys" className="btn-block model-quickstart-cta">
              {qs.createKeyCta}
            </Link>
          </>
        )}
        <p className="note quickstart-env-label">{qs.envBash}</p>
        <CodeSnippet
          code={quickStartApiKeyExportBash()}
          copyLabel={dict.keys.copy}
          copiedLabel={dict.keys.copied}
        />
        <p className="note quickstart-env-label">{qs.envPowerShell}</p>
        <CodeSnippet
          code={quickStartApiKeyExportPowerShell()}
          copyLabel={dict.keys.copy}
          copiedLabel={dict.keys.copied}
        />
      </section>

      <section className="quickstart-step" aria-labelledby="quickstart-step-2">
        <h3 id="quickstart-step-2" className="quickstart-step-heading">
          <span className="quickstart-step-num">2</span>
          {qs.step2Title}
        </h3>
        <p className="note">
          {qs.step2IntroPrefix}{' '}
          <code className="quickstart-model-id">{modelId}</code>{' '}
          {qs.step2IntroSuffix}
        </p>

        {!gatewayReady && <p className="err">{qs.gatewayUnavailable}</p>}

        {gatewayReady && snippet && (
          <>
            <div
              className="quickstart-tabs"
              role="tablist"
              aria-label={qs.step2Title}
              onKeyDown={onTabKeyDown}
            >
              {LANGS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`quickstart-tab-${id}`}
                  aria-selected={lang === id}
                  aria-controls={`quickstart-panel-${id}`}
                  tabIndex={lang === id ? 0 : -1}
                  className={`quickstart-tab${lang === id ? ' is-active' : ''}`}
                  ref={(el) => {
                    tabRefs.current[id] = el;
                  }}
                  onClick={() => setLang(id)}
                >
                  {tabLabel[id]}
                </button>
              ))}
            </div>
            <div
              role="tabpanel"
              id={`quickstart-panel-${lang}`}
              aria-labelledby={`quickstart-tab-${lang}`}
            >
              {lang === 'curl' ? (
                <>
                  <p className="note quickstart-env-label">{qs.envBash}</p>
                  <CodeSnippet
                    code={quickStartSnippet(modelId, 'curl', 'bash') ?? ''}
                    copyLabel={dict.keys.copy}
                    copiedLabel={dict.keys.copied}
                  />
                  <p className="note quickstart-env-label">{qs.envPowerShell}</p>
                  <CodeSnippet
                    code={quickStartSnippet(modelId, 'curl', 'powershell') ?? ''}
                    copyLabel={dict.keys.copy}
                    copiedLabel={dict.keys.copied}
                  />
                </>
              ) : (
                <>
                  {install && (
                    <>
                      <p className="note quickstart-env-label">{qs.installLabel}</p>
                      <CodeSnippet code={install} copyLabel={dict.keys.copy} copiedLabel={dict.keys.copied} />
                    </>
                  )}
                  <CodeSnippet code={snippet} copyLabel={dict.keys.copy} copiedLabel={dict.keys.copied} />
                </>
              )}
            </div>
          </>
        )}
      </section>

      <div className="modal-actions">
        <button type="button" className="ghost" onClick={onClose}>
          {dict.common.close}
        </button>
      </div>
    </div>
  );
}
